import { NextPage } from 'next';
import React from 'react';
import { Header } from 'components/structures';
import {
  architectureEngineeringServices,
  civilConstructionWireless,
  constructionAndTechnicalManagement,
  wirelessBanner,
} from 'images/expertise';
import DetailPage from 'containers/expertise/DetailPage';

const Wireless: NextPage = () => {
  return (
    <>
      <Header
        title="Wireless"
        description="Utilities One offers quality cordless solutions. We have the tools and the expertise necessary to leverage the most sophisticated innovations; follow all industry standards and security requirements; carry out every task in a prompt manner, and satisfy any customer’s needs in full."
        image={wirelessBanner}
      />
      <DetailPage
        listBlocks={[
          {
            bgImage: civilConstructionWireless,
            title: 'Site Development Services',
            id: 'siteDevelopmentServices',
            listAccordionItems: [
              {
                title: 'Program Management',
                description:
                  'Seasoned telecom experts drive our expertise in the wireless site development solutions. Having collectively negotiated, developed, built and upgraded thousands of wireless sites for carriers and tower companies across the United States, Utilities One is ready to handle any project, no matter how complex or sophisticated it may be.',
                listItems: [
                  { textItem: 'Program preparation and scheduling' },
                  { textItem: 'Capital budgeting' },
                  { textItem: 'Agreement settlement, preparation and coordination' },
                  { textItem: 'Supplier choice and management' },
                  { textItem: 'Asset management' },
                  { textItem: 'Value engineering' },
                  { textItem: 'File control' },
                  { textItem: 'Deployment tracking' },
                  { textItem: 'Standardization' },
                  { textItem: 'Quality control' },
                  { textItem: 'Security strategy planning' },
                  { textItem: 'Progress tracking' },
                  { textItem: 'Close-out, launch, hand-off and turn-on' },
                  { textItem: 'System enhancement' },
                  { textItem: 'Upkeep strategy advancement' },
                ],
              },
              {
                title: 'Site Acquisition Services',
                listItems: [
                  { textItem: 'Practical prospects assessment' },
                  { textItem: 'Beneficial business terms settlement' },
                  { textItem: 'Lease execution' },
                  { textItem: 'Audits submission' },
                  { textItem: 'Job scoping management' },
                  { textItem: 'Lease change execution' },
                  { textItem: 'Pre-deployment analysis' },
                  { textItem: 'Site identification' },
                  { textItem: 'Lease settlements' },
                  { textItem: 'Lease adjustments' },
                  { textItem: 'Disintegration and sedimentation strategies' },
                  { textItem: 'DOT Permitting' },
                ],
              },
              {
                title: 'Land Use Services & Permitting',
                manyBlocks: true,
                listBlocks: [
                  {
                    block: 'Permitting Solutions',
                    listBlockItems: [
                      { textBlockItem: 'Process, research study and supply detailed requirements for approval' },
                      { textBlockItem: 'Put together the appropriate files and facilitate their prompt submission' },
                      {
                        textBlockItem:
                          'Consult with all parties in the evaluation procedure to determine, address and fix concerns',
                      },
                    ],
                  },
                  {
                    block: 'Zoning Providers',
                    listBlockItems: [
                      { textBlockItem: 'Conduct pre-project zoning analysis' },
                      { textBlockItem: 'Coordinate applications and paperwork with the suitable jurisdictions' },
                      {
                        textBlockItem: 'Represent customers in public online forums as needed',
                      },
                    ],
                  },
                  {
                    block: 'Additional Solutions',
                    listBlockItems: [
                      {
                        textBlockItem:
                          'Coordinate auxiliary due diligence services consisting of studies, geotechnical services, appraisal services and specific title test services',
                      },
                      {
                        textBlockItem:
                          'Coordinate and support the submission of unique applications with state and federal companies',
                      },
                      {
                        textBlockItem: 'Conduct internal strategy evaluation',
                      },
                      {
                        textBlockItem: 'Coordinate release of real estate title encumbrances',
                      },
                      {
                        textBlockItem: 'Prepare expediency and "site fit" illustrations',
                      },
                    ],
                  },
                ],
              },
              {
                title: 'Building Management Solutions',
                listItems: [
                  { textItem: 'OSP civil design' },
                  { textItem: 'Examine feasible prospect assessment for network improvements' },
                  { textItem: 'Conduct construction budget plan examinations' },
                  { textItem: 'Handle scoping sessions' },
                  { textItem: 'Help engineering in site layout' },
                  { textItem: 'Deal with quote management with general contractors' },
                  { textItem: 'Handle general contractor for all field activities' },
                  { textItem: 'Total closeout plans' },
                ],
              },
            ],
          },
          {
            bgImage: architectureEngineeringServices,
            id: 'architectureEngineeringServices',
            title: 'Architecture & Engineering Services',
            listAccordionItems: [
              {
                title: 'Civil Engineering',
                description:
                  'We work closely with our clients to provide facility designs that fulfill both the functional and aesthetic requirements of a project, whether simple or elaborate. We also provide on-site services, often in conjunction with a local design professional, to ensure that permit acquisition and construction proceed efficiently.',
                listItems: [
                  { textItem: 'Site design' },
                  { textItem: 'Grading and roadway design' },
                  { textItem: 'Culvert design' },
                  { textItem: 'Retaining wall design' },
                  { textItem: 'GIS overlay' },
                  { textItem: 'DEP permitting' },
                  { textItem: 'Zoning assistance' },
                  { textItem: 'Specialist Engineer Statement' },
                ],
              },
              {
                title: 'Structural Engineering',
                listItems: [
                  { textItem: 'Structural evaluations' },
                  { textItem: 'Structural analyses' },
                  { textItem: 'Tower mapping/Foundation mapping' },
                  { textItem: 'Tower adjustment' },
                  { textItem: 'Construction expense quotes' },
                  { textItem: 'Submittal and evaluation of illustration reviews' },
                  { textItem: 'Construction observation and evaluation' },
                  { textItem: 'IBC unique examinations' },
                  { textItem: 'Peer evaluation' },
                  { textItem: 'Worth engineering' },
                  { textItem: 'Lawsuits and witness assistance' },
                  { textItem: 'Forensic examinations' },
                  { textItem: 'Tower structure design' },
                  { textItem: 'Structure upgrades' },
                ],
              },
              {
                title: 'Electrical and Mechanical Engineering',
                listItems: [
                  { textItem: 'Electric service design' },
                  { textItem: 'Electric system evaluations' },
                  { textItem: 'Electrical load research studies' },
                  { textItem: 'Electric service upgrades' },
                  { textItem: 'Generator setups' },
                  { textItem: 'A/C design' },
                  { textItem: 'A/C upgrades and evaluations' },
                  { textItem: 'AC/DC power researches' },
                  { textItem: 'DC system design' },
                ],
              },
              {
                title: 'Fiber Engineering and Permitting',
                listItems: [
                  { textItem: 'Fiber audits and field studies to include of rod and roping' },
                  { textItem: 'Pole accessories and licensing applications' },
                  { textItem: 'Make-ready design' },
                  { textItem: 'New lateral design' },
                  { textItem: 'New moving design' },
                  { textItem: 'New manhole design' },
                  { textItem: 'OCALC structural reports' },
                  { textItem: 'Lateral street permitting' },
                  { textItem: 'Street tenancy permitting' },
                ],
              },
              {
                title: 'Environmental and Geotechnical Services',
                listItems: [
                  { textItem: 'Lead paint and asbestos research studies' },
                  { textItem: 'Area 106 research studies' },
                  { textItem: 'NEPA reports' },
                  { textItem: 'Geotechnical analyses' },
                  { textItem: 'Soil resistivity evaluation' },
                  { textItem: 'Construction stage evaluation' },
                ],
              },
              {
                title: 'Visual Analyses and Mapping Services',
                listItems: [
                  { textItem: 'Area mapping' },
                  { textItem: '3D modelling services' },
                  { textItem: 'Photo simulations' },
                  { textItem: 'Visual effect reports' },
                  { textItem: 'Field verified surface analysis' },
                  { textItem: 'Specialist visual effect statement' },
                ],
              },
            ],
          },
          {
            bgImage: constructionAndTechnicalManagement,
            id: 'constructionAndTechnicalManagement',
            title: 'Construction and Technical Management',
            listAccordionItems: [
              {
                title: 'Tower Setup & Upkeep Solutions',
                description:
                  'Our Wireless construction professionals all have what it takes to design and build cutting-edge telecommunications facilities. Our experience, flexibility, quality processes and focus on safety procedures, allow Utilities One to reach unprecedented results within the very least amount of time possible. Our Construction Managers understand the needs of the customers better than anyone else working solely around the client’s schedule and not vice versa.',
                listItems: [
                  { textItem: 'Roadway construction' },
                  { textItem: 'Tower construction' },
                  { textItem: 'Self-supporting, monopoles, guyed and structural retrofit jobs' },
                  {
                    textItem:
                      'Specialized antenna mount fabrication; Waveguide bridge & ladder; Setup; Antenna confirmation & coax sweep screening; Azimuth & down-tilts',
                  },
                  { textItem: 'Antenna system setup' },
                  {
                    textItem:
                      'Interior grounding; Cable drop setup; Devices rack fabrication & setup; Battery setup; Radio setup',
                  },
                  { textItem: 'System efficiency upgrades' },
                  { textItem: 'Reconfigurations; Sweep and PIM screening; OTDR and CPRI screening; Sectorizations' },
                  { textItem: 'Tower inspections and central evaluations' },
                  { textItem: 'Tower lighting systems; incandescent, medium/high strength strobe and LED lighting' },
                ],
              },
              {
                title: 'DAS Cell Design & Application',
                listItems: [
                  { textItem: 'Evaluation of RF requirements' },
                  { textItem: 'Cells and DAS setups' },
                  { textItem: 'RF design' },
                  { textItem: 'Regulatory services' },
                  { textItem: 'Fiber network application and coordination' },
                  { textItem: 'Fiber OSP design, construction, and make-ready' },
                  { textItem: 'Construction combination and optimization' },
                  { textItem: 'Stealth solutions' },
                  { textItem: 'Troubleshooting & repair work' },
                ],
              },
              {
                title: 'Building and Construction Support Services',
                listItems: [
                  { textItem: 'Warehousing/ Circulation/ Product Handling' },
                  { textItem: 'Web-enabled stock tracking' },
                  { textItem: 'Site kitting' },
                  { textItem: 'Product sourcing/ Processing' },
                  { textItem: 'On-site shipment and setup' },
                  { textItem: 'Pre-assembly & screening' },
                  { textItem: 'System screening' },
                ],
              },
            ],
          },
        ]}
      />
    </>
  );
};

export default Wireless;
